import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { Col, Dropdown, DropdownMenu, DropdownToggle, Row, Spinner, UncontrolledTooltip } from "reactstrap";
import SimpleBar from "simplebar-react";
import { useNotifications } from "features/Notifications/hooks/useNotifications";
import { useNotificationPreview } from "features/Notifications/context/NotificationPreviewContext";
import {
  NOTIFICATION_BADGE_CAP,
  NOTIFICATION_CATEGORY_ICON,
  NOTIFICATION_DEFAULT_ICON,
  NOTIFICATION_SOURCE_ROUTES,
} from "features/Notifications/constants/notification.constants";
import { NotificationCategory, NotificationSourceType } from "features/Notifications/types/notification.types";
import type { Notification } from "features/Notifications/types/notification.types";
import Avatar from "components/common/Avatar";

const ACTOR_AVATAR_CATEGORIES = new Set<string>([
  NotificationCategory.LEAVE_APPLICATION,
]);

function getIcon(category: string) {
  return NOTIFICATION_CATEGORY_ICON[category as NotificationCategory] ?? NOTIFICATION_DEFAULT_ICON;
}

function resolveRoute(n: Notification): string | null {
  if (!n.sourceType || !n.sourceId) return null;
  const base = NOTIFICATION_SOURCE_ROUTES[n.sourceType as NotificationSourceType];
  if (!base) return null;
  return `${base}/${n.sourceId}`;
}

function timeAgo(iso: string | null | undefined): string {
  if (!iso) return "";
  const m = moment(iso);
  if (!m.isValid()) return "";
  return m.fromNow();
}

const NotificationDropdown = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const simpleBarRef = useRef<any>(null);
  const { addPreview } = useNotificationPreview();

  const {
    notifications,
    unreadCount,
    loading,
    hasMore,
    markRead,
    markAllRead,
    loadMore,
  } = useNotifications({ panelOpen: open, onNewNotification: addPreview });

  const handleItemClick = (n: Notification) => {
    void markRead(n.id);
    const route = resolveRoute(n);
    if (route) {
      setOpen(false);
      navigate(route);
    }
  };

  useEffect(() => {
    const el = simpleBarRef.current?.getScrollElement();
    if (!el) return;
    const onScroll = () => {
      if (loading || !hasMore) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 60) {
        loadMore();
      }
    };
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [loading, hasMore, loadMore, open]);

  const badgeLabel = unreadCount > NOTIFICATION_BADGE_CAP ? `${NOTIFICATION_BADGE_CAP}+` : unreadCount > 0 ? String(unreadCount) : null;

  return (
    <React.Fragment>
      <Dropdown
        isOpen={open}
        toggle={() => setOpen((v) => !v)}
        className="topbar-head-dropdown ms-1 header-item"
      >
        <DropdownToggle
          type="button"
          tag="button"
          className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle"
        >
          <i className="bx bx-bell fs-22" />
          {badgeLabel && (
            <span className="position-absolute topbar-badge fs-10 translate-middle badge rounded-pill bg-danger">
              {badgeLabel}
              <span className="visually-hidden">unread notifications</span>
            </span>
          )}
        </DropdownToggle>

        <DropdownMenu className="dropdown-menu-lg dropdown-menu-end p-0">
          <div className="dropdown-head bg-primary bg-pattern rounded-top">
            <div className="p-3">
              <Row className="align-items-center">
                <Col>
                  <h6 className="m-0 fs-16 fw-semibold text-white">Notifications</h6>
                </Col>
                {unreadCount > 0 && (
                  <div className="col-auto dropdown-tabs">
                    <span className="badge bg-light-subtle text-body fs-13">
                      {unreadCount} New
                    </span>
                  </div>
                )}
              </Row>
            </div>
          </div>

          <SimpleBar ref={simpleBarRef} style={{ maxHeight: "300px" }} className="p-2">
            {loading && notifications.length === 0 ? (
              <div className="text-center py-4">
                <Spinner size="sm" color="primary" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center text-muted py-4 small">
                <i className="bx bx-bell-off fs-24 d-block mb-1" />
                No notifications yet
              </div>
            ) : (
              <>
                {notifications.map((n) => {
                  const { icon, cls } = getIcon(n.category);
                  const route = resolveRoute(n);
                  return (
                    <div
                      key={n.id}
                      role={route ? "button" : undefined}
                      className={`text-reset notification-item d-block dropdown-item position-relative ${!n.isRead ? "fw-semibold bg-light" : ""}`}
                      onClick={() => void handleItemClick(n)}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="d-flex">
                        <div id={`notif-avatar-${n.id}`} className="avatar-xs me-3 flex-shrink-0">
                          {n.actor && ACTOR_AVATAR_CATEGORIES.has(n.category) ? (
                            <>
                              <Avatar
                                name={`${n.actor.firstName} ${n.actor.lastName}`}
                                image={n.actor.avatar}
                              />
                              <UncontrolledTooltip target={`notif-avatar-${n.id}`} placement="top">
                                {n.actor.firstName} {n.actor.lastName}
                              </UncontrolledTooltip>
                            </>
                          ) : (
                            <span className={`avatar-title rounded-circle fs-16 ${cls}`}>
                              <i className={icon} />
                            </span>
                          )}
                        </div>
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-start">
                            <h6 className="mt-0 mb-1 fs-13 fw-semibold">{n.title}</h6>
                            {!n.isRead && (
                              <span
                                className="badge bg-primary rounded-pill ms-2 flex-shrink-0"
                                style={{ fontSize: "0.6rem", padding: "2px 5px" }}
                              >
                                New
                              </span>
                            )}
                          </div>
                          {n.message && (
                            <p className="mb-1 fs-13 text-muted">{n.message}</p>
                          )}
                          <p className="mb-0 fs-11 fw-medium text-muted">
                            <i className="mdi mdi-clock-outline me-1" />
                            {timeAgo(n.created_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {hasMore && loading ? (
                  <div className="text-center py-2">
                    <Spinner size="sm" color="primary" />
                  </div>
                ) : !hasMore && (
                  <div className="text-center text-muted py-2" style={{ fontSize: "0.75rem" }}>
                    <i className="mdi mdi-check-circle-outline me-1" />
                    You&apos;re all caught up
                  </div>
                )}
              </>
            )}
          </SimpleBar>

          {notifications.some((n) => !n.isRead) && (
            <div className="my-1 text-center">
              <button
                type="button"
                className="btn btn-link btn-sm text-muted"
                onClick={() => void markAllRead()}
              >
                <i className="mdi mdi-check-all me-1" />
                Mark all as read
              </button>
            </div>
          )}
        </DropdownMenu>
      </Dropdown>
    </React.Fragment>
  );
};

export default NotificationDropdown;
